<template>
  <vl-grid v-vl-spacer:bottom.medium v-vl-spacer:top.medium>
    <vl-column v-vl-spacer:bottom.medium width="3" width-m="12">
      <vl-checkbox v-model="data.onlyCompleted" mod-switch>{{
        $t('publications.versions.list.filter.onlyCompleted')
      }}</vl-checkbox>
    </vl-column>
    <vl-column>
      <vl-data-table-extended
        :data="items"
        :columns="columnMap"
        :fetching="loading"
        :meta="metaData"
        mod-clickable-rows
        mod-zebra
        @column-clicked="columnClicked"
        @pager-clicked="pagerClicked"
      >
        <template v-slot:noresults>
          <template v-if="loading">
            <vl-region>
              <div v-vl-align:center>
                <vl-loader :message="$t('publications.versions.list.loading')" />
              </div>
            </vl-region>
          </template>
          <template v-else>
            <div class="vl-u-spacer--large">
              <p>{{ $t('publications.versions.list.table.noResults.subtitle') }}</p>
            </div>
          </template>
        </template>
      </vl-data-table-extended>
    </vl-column>
  </vl-grid>
</template>
<script lang="ts" setup>
import { usePublicationVersionListStore } from '@/modules/publications/store/publication-version-list.store';
import { h, onMounted, onUnmounted, watch } from 'vue';
import { computed } from 'vue';
import { ColumnDefExtended } from '@/components/vl-data-table-extended/types';
import { RowData } from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { PublicationVersionSearchResult } from '@/api/frontoffice/clients';
import { dateFilter, dateTimeFilter } from '@/filters';
import { reactive } from 'vue';
import { PublicationSessionStorageCommon, PublicationVersionState } from '@/modules/publications/common';

const { t } = useI18n();
const store = usePublicationVersionListStore();

const data = reactive({
  onlyCompleted: false
});

const props = defineProps<{
  publicationId: string;
}>();

const loading = computed((): boolean => {
  return store.loading;
});

const items = computed((): PublicationVersionSearchResult & { routerLink?: string }[] => {
  return store.publications?.results?.map((i) => {
    return {
      ...i,
      routerLink: `/publication/${props.publicationId}/version/${i.id}`
    };
  });
});

const metaData = computed(() => {
  return store.metaData;
});

watch(
  () => data.onlyCompleted,
  (newValue) => {
    PublicationSessionStorageCommon.set('onlyCompleted', newValue);
    doSearch();
  }
);

const columnMap = computed((): ColumnDefExtended<RowData>[] => {
  return [
    {
      accessorKey: 'creationDate',
      header: t('publications.versions.list.table.header.creationDate'),
      enableSorting: true,
      sortDescFirst: true,
      size: 10,
      link: true,
      cell: (props: any) => {
        const dateString = props.row.original.creationDate;
        return dateTimeFilter(dateString);
      }
    },
    {
      accessorKey: '',
      header: t('publications.versions.list.table.header.description'),
      enableSorting: false,
      size: 40,
      link: true,
      cell: (props: any) => {
        const description = props.row.original.versionDescription;
        if (description) {
          return description;
        }
        return '-';
      }
    },
    {
      accessorKey: '',
      header: t('publications.versions.list.table.header.status'),
      enableSorting: false,
      link: true,
      size: 10,
      cell: (props: any) => {
        const versionState = new PublicationVersionState(props.row.original);
        return h(
          'span',
          {
            class: versionState.isInError ? 'status--error' : ''
          },
          [versionState.description ? versionState.description : '-']
        );
      }
    },
    {
      accessorKey: 'productEndExecution',
      header: t('publications.versions.list.table.header.publicationDate'),
      enableSorting: true,
      sortDescFirst: true,
      size: 2,
      link: true,
      cell: (props: any) => {
        const statusOk = props.row.original.productStatusName === 'Verwerkt';
        const dateString = props.row.original.productEndExecution;
        return dateString && statusOk ? dateFilter(dateString) : '-';
      }
    }
  ];
});

onMounted(async () => {
  loadSessionStorage();

  if (props.publicationId) {
    await doSearch();
  }
});

onUnmounted(() => {
  store.clear();
});

const loadSessionStorage = () => {
  data.onlyCompleted = PublicationSessionStorageCommon.get('onlyCompleted') === true;
};

const doSearch = async () => {
  store.updateFilter({    
    onlyCompleted: data.onlyCompleted
  });
  await search({ resetPager: true });
};

const search = async ({ resetPager }: { resetPager: boolean } = { resetPager: false }) => {
  if (resetPager) {
    store.resetPager();
  }

  await store.getPublicationVersions(props.publicationId);
};

const pagerClicked = (pageNumber: number) => {
  store.updatePage(pageNumber);
  search();
};

const columnClicked = (evt: { key: string; direction: string }) => {
  store.updateSorting(evt.key, evt.direction);
  search({ resetPager: true });
};
</script>

<style lang="scss" scoped>
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colorsRaw.scss';
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colors.scss';

:deep(.vl-data-table tbody .status--error) {
  color: $vl-error-color;
}
</style>
